import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import ProductPicker from "../products/productPicker";

import HttpManager from "../../../../services/HttpManager";
import { Empty, Loading, apiError, daysAgo, monthStart, today, ui, useCampus, useIdleLocked } from "../kits/shared";

/*
	Tienda → Márgenes (sección "productMargins", 2026-09-30). Solo Super Admin, Contraloría y los roles a los que se les dé.
	Se puede vender con pérdida o con margen bajo; esto es para verlo y corregir precios o compras.
*/

type Tab = "loss" | "low" | "prices" | "profit" | "simulate" | "targets";

const pct = (value: number | null): string => value === null ? "-" : (value * 100).toFixed(1) + "%";

const Margenes: React.FC = () => {
	const { campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const http = HttpManager.getInstance().inventoryService;
	const [tab, setTab] = useState<Tab>("loss");
	const [from, setFrom] = useState<string>(daysAgo(90));
	const [to, setTo] = useState<string>(today());
	const [sales, setSales] = useState<any | null>(null);
	const [prices, setPrices] = useState<any[] | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!campusId) return;
		setLoading(true);
		setError(null);
		try {
			if (tab === "profit" || tab === "simulate" || tab === "targets") {
				return;
			}
			if (tab === "prices") {
				setPrices((await http.riskyPrices(campusId)).data);
			} else {
				setSales((await http.marginSales(campusId, { from, to, band: tab })).data);
			}
		} catch (requestError: any) {
			setError(requestError?.response?.data?.message || "No se pudo cargar.");
		} finally {
			setLoading(false);
		}
	}, [campusId, tab, from, to]);
	useEffect(() => { load(); }, [load]);

	const money = (value: number | null) => value === null ? "-" : currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	const counts = sales?.counts || {};
	const tabs: { key: Tab; label: string; count?: number }[] = [
		{ key: "loss", label: "Vendidos con pérdida", count: counts.loss },
		{ key: "low", label: "Margen menor a 20%", count: counts.low },
		{ key: "prices", label: "Precios en riesgo" },
		{ key: "profit", label: "Utilidad" },
		{ key: "simulate", label: "Simulador" },
		{ key: "targets", label: "Margen objetivo" }
	];

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	return <div style={ui.page}>
		<h2 style={ui.title}>Márgenes</h2>
		<div style={ui.tabs}>
			{tabs.map((entry) => <button key={entry.key} type="button" disabled={idleLocked}
				className={"btn rounded-pill " + (entry.key === tab ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab(entry.key)}>
				{entry.label}{entry.count ? <span style={ui.badge(entry.key === tab)}>{entry.count}</span> : null}
			</button>)}
		</div>
		{tab === "profit" ? <Profit campusId={campusId} money={money} /> : tab === "simulate" ? <Simulator campusId={campusId} money={money} /> : tab === "targets" ? <Targets campusId={campusId} /> : <>
		{tab !== "prices" ? <div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("160px")}><span style={ui.caption}>Vendido desde</span>
				<input type="date" className="form-control" style={ui.input} value={from} disabled={idleLocked} onChange={(event) => setFrom(event.target.value)} /></div>
			<div style={ui.field("160px")}><span style={ui.caption}>Hasta</span>
				<input type="date" className="form-control" style={ui.input} value={to} disabled={idleLocked} onChange={(event) => setTo(event.target.value)} /></div>
			{sales ? <span style={{ ...ui.small, marginLeft: "auto" }}>{(counts.ok || 0) + (counts.low || 0) + (counts.loss || 0)} ventas con costo · {counts.noCost || 0} todavía sin costo de compra</span> : null}
		</div> : null}
		<div style={{ ...ui.card, overflowX: "auto", marginTop: "10px" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: tab === "prices" ? (!prices || prices.length < 1 ? <Empty text="Ningún precio deja menos de 20%." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Producto</th>
						<th style={{ ...ui.th, ...ui.right }}>Precio de venta</th>
						<th style={{ ...ui.th, ...ui.right }}>Costo de compra</th>
						<th style={{ ...ui.th, ...ui.right }}>Margen</th>
						<th style={{ ...ui.th, ...ui.right }}>%</th>
						<th style={{ ...ui.th, ...ui.right }}>Objetivo</th>
						<th style={{ ...ui.th, ...ui.right }}>Precio al objetivo</th>
					</tr></thead>
					<tbody>{prices.map((row) => <tr key={row.articleId}>
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode} · {row.kind}</div></td>
						<td style={{ ...ui.td, ...ui.right }}>{money(row.price)}</td>
						<td style={{ ...ui.td, ...ui.right }}>{money(row.cost)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 600 }}>{money(row.margin)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 700 }}>{pct(row.marginPct)}</td>
						<td style={{ ...ui.td, ...ui.right }}>{pct(row.target)}</td>
						<td style={{ ...ui.td, ...ui.right, fontWeight: 600 }}>{money(row.priceForTarget)}</td>
					</tr>)}</tbody>
				</table>)
				: !sales || sales.rows.length < 1 ? <Empty text={tab === "loss" ? "Ninguna venta con pérdida en estas fechas." : "Ninguna venta con margen menor a 20% en estas fechas."} />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Vendido</th>
						<th style={ui.th}>Alumno</th>
						<th style={ui.th}>Producto</th>
						<th style={{ ...ui.th, ...ui.right }}>Cobrado</th>
						<th style={{ ...ui.th, ...ui.right }}>Costo</th>
						<th style={{ ...ui.th, ...ui.right }}>Margen</th>
						<th style={{ ...ui.th, ...ui.right }}>%</th>
					</tr></thead>
					<tbody>{sales.rows.map((row: any) => <tr key={row.kitId}>
						<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.date}<div style={ui.tiny}>{row.campus}</div></td>
						<td style={ui.td}>{row.student}</td>
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.product}{row.quantity > 1 ? " ×" + row.quantity : ""}</div><div style={ui.tiny}>{row.waCode}</div></td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{money(row.revenue)}{row.discount > 0 ? <div style={ui.tiny}>lista {money(row.listPrice)} · desc. {money(row.discount)}</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{money(row.cost)}<div style={ui.tiny}>{row.costSource}</div></td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 600 }}>{money(row.margin)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 700 }}>{pct(row.marginPct)}</td>
					</tr>)}</tbody>
				</table>}
		</div>
		</>}
	</div>;
};

const Profit: React.FC<{ campusId: number; money: (value: number | null) => string }> = ({ campusId, money }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const [from, setFrom] = useState<string>(monthStart());
	const [to, setTo] = useState<string>(today());
	const [groupBy, setGroupBy] = useState<string>("product");
	const [data, setData] = useState<any | null>(null);
	const [expenses, setExpenses] = useState<any[]>([]);
	const [month, setMonth] = useState<string>(today().slice(0, 7));
	const [concept, setConcept] = useState<string>("salaries");
	const [amount, setAmount] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const load = useCallback(() => {
		setData(null);
		http.profit(campusId, { from, to, groupBy }).then((response) => setData(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo calcular.")));
		http.expenses(campusId).then((response) => setExpenses(response.data)).catch(() => undefined);
	}, [campusId, from, to, groupBy]);
	useEffect(() => { load(); }, [load]);
	const pctColor = (value: number | null) => value === null ? undefined : value < 0 ? "var(--wa-oro)" : value < 0.2 ? "var(--wa-oro)" : "var(--wa-verde)";
	const totals = data?.totals;
	return <>
		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("150px")}><span style={ui.caption}>Vendido desde</span><input type="date" className="form-control" style={ui.input} value={from} onChange={(event) => setFrom(event.target.value)} /></div>
			<div style={ui.field("150px")}><span style={ui.caption}>Hasta</span><input type="date" className="form-control" style={ui.input} value={to} onChange={(event) => setTo(event.target.value)} /></div>
			<div style={{ display: "flex", gap: "6px" }}>
				{[["product", "Por producto"], ["campus", "Por plantel"], ["advisor", "Por vendedora"]].map(([key, label]) =>
					<button key={key} type="button" className={"btn btn-sm rounded-pill " + (groupBy === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setGroupBy(key)}>{label}</button>)}
			</div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		{totals ? <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "10px 0" }}>
			<div style={ui.kpi}><div style={ui.caption}>Vendido</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{money(totals.sales)}</div><div style={ui.tiny}>cobrado {money(totals.collected)}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Costo de lo vendido</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{money(totals.cost)}</div>{totals.withoutCost ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{totals.withoutCost} ventas sin costo de compra</div> : null}</div>
			<div style={ui.kpi}><div style={ui.caption}>Utilidad bruta</div><div style={{ fontSize: "20px", fontWeight: 700, color: pctColor(totals.grossPct) }}>{money(totals.gross)}</div><div style={ui.tiny}>{pct(totals.grossPct)}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Gastos del departamento</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{money(totals.expenses)}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Utilidad neta</div><div style={{ fontSize: "20px", fontWeight: 700, color: pctColor(totals.netPct) }}>{money(totals.net)}</div><div style={ui.tiny}>{pct(totals.netPct)}</div></div>
		</div> : null}
		<div style={{ ...ui.card, overflowX: "auto" }}>
			{data === null ? <Loading /> : data.rows.length < 1 ? <Empty text="Sin ventas del catálogo nuevo en estas fechas." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					<th style={ui.th}>{groupBy === "product" ? "Producto" : groupBy === "campus" ? "Plantel" : "Vendedora"}</th>
					<th style={{ ...ui.th, ...ui.right }}>Piezas</th>
					<th style={{ ...ui.th, ...ui.right }}>Vendido</th>
					<th style={{ ...ui.th, ...ui.right }}>Costo</th>
					<th style={{ ...ui.th, ...ui.right }}>Utilidad bruta</th>
					<th style={{ ...ui.th, ...ui.right }}>%</th>
					<th style={{ ...ui.th, ...ui.right }}>Gastos</th>
					<th style={{ ...ui.th, ...ui.right }}>Utilidad neta</th>
					<th style={{ ...ui.th, ...ui.right }}>%</th>
				</tr></thead>
				<tbody>{data.rows.map((row: any) => <tr key={row.name}>
					<td style={ui.td}><b>{row.name}</b>{row.withoutCost ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{row.withoutCost} sin costo</div> : null}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.units}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(row.sales)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(row.cost)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 600 }}>{money(row.gross)}</td>
					<td style={{ ...ui.td, ...ui.right, color: pctColor(row.grossPct), fontWeight: 700 }}>{pct(row.grossPct)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(row.expenses)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 600 }}>{money(row.net)}</td>
					<td style={{ ...ui.td, ...ui.right, color: pctColor(row.netPct), fontWeight: 700 }}>{pct(row.netPct)}</td>
				</tr>)}</tbody>
			</table>}
		</div>
		<div style={{ ...ui.card, padding: "10px 12px", marginTop: "12px" }}>
			<div style={{ fontWeight: 700, marginBottom: "6px" }}>Gastos del departamento por mes</div>
			<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
				<div style={ui.field("140px")}><span style={ui.caption}>Mes</span><input type="month" className="form-control" style={ui.input} value={month} onChange={(event) => setMonth(event.target.value)} /></div>
				<div style={ui.field("220px")}><span style={ui.caption}>Concepto</span>
					<select className="form-select" style={ui.input} value={concept} onChange={(event) => setConcept(event.target.value)}>
						<option value="salaries">Salarios del departamento</option><option value="bonuses">Bonos y comisiones</option><option value="other">Otros gastos</option>
					</select></div>
				<div style={ui.field("150px")}><span style={ui.caption}>Monto</span><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={amount} onChange={(event) => setAmount(event.target.value)} /></div>
				<button type="button" className={ui.btnPrimary} style={{ height: "34px" }} disabled={!(Number(amount) > 0)}
					onClick={() => http.saveExpense(campusId, { month, concept, amount }).then((response) => { setExpenses(response.data); setAmount(""); load(); }).catch((requestError) => setError(apiError(requestError, "No se pudo guardar.")))}>Guardar</button>
			</div>
			{expenses.map((row) => <div key={row.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<span>{row.month} · {row.conceptName}</span>
				<span>{money(row.amount)} {currentUser ? <button type="button" className="btn btn-link btn-sm" style={{ padding: "0 4px", color: "var(--wa-apagado)" }}
					onClick={() => http.deleteExpense(campusId, row.id).then((response) => { setExpenses(response.data); load(); })}>✕</button> : null}</span>
			</div>)}
		</div>
	</>;
};

const Simulator: React.FC<{ campusId: number; money: (value: number | null) => string }> = ({ campusId, money }) => {
	const [data, setData] = useState<any | null>(null);
	const [price, setPrice] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const cost = data?.cost ?? null;
	const simulated = Number(price) > 0 && cost !== null ? (Number(price) - cost) / Number(price) : null;
	return <div style={{ ...ui.card, padding: "12px" }}>
		<ProductPicker campusId={campusId} kinds={["article", "kit", "uniform"]} placeholder="Elegir producto…"
			onPick={(product: any) => { setError(null); HttpManager.getInstance().stockOpsService.simulate(campusId, product.id).then((response) => { setData(response.data); setPrice(String(response.data.price || "")); }).catch((requestError) => setError(apiError(requestError, "No se pudo simular."))); }} />
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		{data ? <>
			<h5 style={{ marginTop: "12px" }}>{data.name} <span style={ui.small}>{data.waCode} · {data.kindName}</span></h5>
			<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
				<div style={ui.kpi}><div style={ui.caption}>Costo de compra</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{money(cost)}</div>{cost === null ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>Falta costo de alguna pieza</div> : null}</div>
				<div style={ui.kpi}><div style={ui.caption}>Precio hoy</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{data.pricePending ? "Por aprobar" : money(data.price)}</div><div style={ui.tiny}>margen {pct(data.marginPct)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Objetivo {pct(data.target)}</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{money(data.priceForTarget)}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Probar precio</div>
					<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={price} onChange={(event) => setPrice(event.target.value)} />
					<div style={{ ...ui.tiny, fontWeight: 700, color: simulated === null ? undefined : simulated < Math.max(0.2, data.target || 0) ? "var(--wa-oro)" : "var(--wa-verde)" }}>margen {pct(simulated)}</div></div>
			</div>
			{data.components.length > 0 ? <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
				<thead><tr><th style={ui.th}>Pieza</th><th style={{ ...ui.th, ...ui.right }}>Cantidad</th><th style={{ ...ui.th, ...ui.right }}>Costo unitario</th><th style={{ ...ui.th, ...ui.right }}>Costo</th></tr></thead>
				<tbody>{data.components.map((row: any, index: number) => <tr key={index} style={{ opacity: row.optional ? 0.6 : 1 }}>
					<td style={ui.td}>{row.name}{row.optional ? <span style={ui.tiny}> · opcional</span> : null}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.quantity}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.unitCost === null ? <span style={{ color: "var(--wa-oro)" }}>sin compra</span> : money(row.unitCost)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(row.cost)}</td>
				</tr>)}</tbody>
			</table> : null}
		</> : null}
	</div>;
};

const TARGET_KINDS = [["kit", "Kits"], ["uniform", "Uniformes"], ["article", "Artículos"], ["digital", "Digitales"], ["service", "Servicios y cobros"]];

const Targets: React.FC<{ campusId: number }> = ({ campusId }) => {
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuper = currentUser?.currentRole === "superAdmin";
	const [values, setValues] = useState<Record<string, string>>({});
	const [saved, setSaved] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		HttpManager.getInstance().stockOpsService.targets(campusId).then((response) => {
			const next: Record<string, string> = {};
			Object.entries(response.data).forEach(([kind, value]) => { next[kind] = String(Math.round(Number(value) * 1000) / 10); });
			setValues(next);
		}).catch(() => undefined);
	}, [campusId]);
	const save = () => {
		setError(null);
		const payload: Record<string, number> = {};
		Object.entries(values).forEach(([kind, value]) => { payload[kind] = Number(value); });
		HttpManager.getInstance().stockOpsService.saveTargets(campusId, payload).then(() => setSaved(true)).catch((requestError) => setError(apiError(requestError, "No se pudo guardar.")));
	};
	return <div style={{ ...ui.card, padding: "12px", maxWidth: "460px" }}>
		{TARGET_KINDS.map(([kind, label]) => <div key={kind} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
			<span>{label}</span>
			<span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
				<input type="number" min={0} max={95} step="0.5" className="form-control" style={{ ...ui.input, width: "90px" }} value={values[kind] ?? ""} disabled={!isSuper}
					onChange={(event) => { setSaved(false); setValues({ ...values, [kind]: event.target.value }); }} /> %
			</span>
		</div>)}
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px", padding: "6px 10px" }}>{error}</div> : null}
		{isSuper ? <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", alignItems: "center", marginTop: "10px" }}>
			{saved ? <span style={{ ...ui.small, color: "var(--wa-verde)" }}>Guardado</span> : null}
			<button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={save}>Guardar</button>
		</div> : null}
	</div>;
};

export default Margenes;
